import React, {useLayoutEffect, useRef} from 'react';

import {Icon} from '@albert-io/atomic';
import {useFluxStore} from 'client/framework';

import {hideModal} from './ModalActions';
import modalStore from './ModalStore';

interface Props {
  children?: React.ReactNode;
  headerClass?: string;
  headerString?: string;
  hideHeader?: boolean;
  /** Required: both the store lookup and `hideModal` are keyed on it. */
  modalKey: string;
  modalWrapperClass?: string;
  modalClass?: string;
  onClose?: () => void;
}

interface ModalContainerProps extends Omit<Props, 'children'> {
  isModalDisplayed?: boolean;
  tail?: React.ReactNode;
}

interface ModalContentProps {
  children?: React.ReactNode;
  modalContentClass?: string;
}

/**
 * Locks or releases the page behind an open modal. Reads the store directly rather
 * than through the bridge: this runs from an effect, not render.
 */
function lockBody(): void {
  const body = global.document.getElementsByTagName('body')[0];
  const html = global.document.getElementsByTagName('html')[0];
  if (modalStore.hasActiveModal()) {
    html.classList.add('has-active-modal');
    body.classList.add('has-active-modal');
  } else {
    html.classList.remove('has-active-modal');
    body.classList.remove('has-active-modal');
  }
}

export default function Modal({
  children,
  headerClass,
  headerString,
  hideHeader,
  modalKey,
  modalWrapperClass,
  modalClass,
  onClose
}: Props) {
  const isModalDisplayed = useFluxStore(modalStore, (store) => store.isModalDisplayed(modalKey));
  const hasRendered = useRef(false);

  // Reproduces `componentDidUpdate`: the scroll lock is re-evaluated after every
  // update but NOT on mount, hence the first-render guard. It stays a layout effect
  // because `componentDidUpdate` ran before paint — deferring it to `useEffect`
  // would let the page behind the modal paint unlocked for a frame.
  useLayoutEffect(() => {
    if (!hasRendered.current) {
      hasRendered.current = true;
      return;
    }
    lockBody();
  });

  return (
    <ModalContainer
      isModalDisplayed={isModalDisplayed}
      headerClass={headerClass}
      headerString={headerString}
      hideHeader={hideHeader}
      modalKey={modalKey}
      modalWrapperClass={modalWrapperClass}
      modalClass={modalClass}
      tail={children}
      onClose={onClose}
    />
  );
}

function ModalContainer({
  isModalDisplayed,
  headerClass = '',
  headerString,
  hideHeader = false,
  modalKey,
  modalWrapperClass = '',
  modalClass = '',
  tail,
  onClose = () => {}
}: ModalContainerProps) {
  const closeModal = () => {
    onClose();
    hideModal(modalKey);
  };

  // @todo add escape key support to close it
  const closeModalOnEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.which === 13) {
      closeModal();
    }
  };

  if (!isModalDisplayed) {
    return null;
  }

  return (
    <div className={`modal ${modalClass}`}>
      <div className='modal__shade' onClick={closeModal} />
      <div className='modal__overflow-handler'>
        <div className={`modal__wrapper ${modalWrapperClass}`} onClick={(e) => e.stopPropagation()}>
          {!hideHeader ? (
            <div className={`modal__header ${headerClass}`}>
              <div className='modal__header-label'>{headerString}</div>
              <div
                className='modal__header-close-btn'
                role='button'
                aria-label='Close'
                tabIndex={0}
                onKeyPress={closeModalOnEnter}
                onClick={closeModal}
              >
                <Icon icon='xmark' />
              </div>
            </div>
          ) : null}
          <div className='modal__body'>{tail}</div>
        </div>
      </div>
    </div>
  );
}

export function ModalContent({children, modalContentClass}: ModalContentProps) {
  const contentClass = modalContentClass || '';
  return <div className={`modal__content ${contentClass}`}>{children}</div>;
}
